import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePreventionAlerts } from 'hooks/usePreventionAlerts';
import { GOPCode, INFLUENZA_ALERT_KEY, type PreventionAlert } from 'types/preventionAlerts';
import { AlertItem } from './AlertItem';
import { GeriatricRow } from './GeriatricRow';
import { InfluenzaRow } from './InfluenzaRow';
import { LastPerformedRow } from './LastPerformedRow';
import { OneTimeRow } from './OneTimeRow';
import { PreventionRow } from './PreventionRow';
import { AlertsContainer, AlertsList, AlertSkeletonIcon } from './PreventionAlertsBar.styles';
import {
  CollapsedIcons,
  RowList,
  SectionAccordion,
  SectionCard,
  SectionContent,
  SectionItem,
  SectionTrigger,
  TriggerRow,
  TriggerTitle,
} from './DuePreventionsSection.styles';

const LAST_PERFORMED_GOPS: (GOPCode | null)[] = [GOPCode.GOP_01732];
const ONE_TIME_GOPS: (GOPCode | null)[] = [
  GOPCode.GOP_01734,
  GOPCode.GOP_01740,
  GOPCode.GOP_01747,
  GOPCode.GOP_01748,
];
const ITEM_VALUE = 'preventions';
const STORAGE_PREFIX = 'preventions-open:';

const readStoredOpen = (storageKey: string, fallback: boolean): boolean => {
  try {
    const stored = window.localStorage.getItem(STORAGE_PREFIX + storageKey);
    return stored === null ? fallback : stored === '1';
  } catch {
    return fallback;
  }
};

const writeStoredOpen = (storageKey: string, open: boolean): void => {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + storageKey, open ? '1' : '0');
  } catch {
    // Browser storage is a convenience only; the screen default applies when it is unavailable.
  }
};

interface DuePreventionsSectionProps {
  medatixxPatientId?: string | null;
  interactionId?: string;
  storageKey: string;
  defaultOpen?: boolean;
  onRecordInfluenza?: (alert: PreventionAlert) => void;
}

export const DuePreventionsSection: React.FC<DuePreventionsSectionProps> = ({
  medatixxPatientId,
  interactionId,
  storageKey,
  defaultOpen = false,
  onRecordInfluenza,
}) => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = usePreventionAlerts(medatixxPatientId, interactionId);
  const [open, setOpen] = useState(() => readStoredOpen(storageKey, defaultOpen));

  if (isLoading) {
    return (
      <AlertsContainer role="status" aria-label={t('general.loading')} data-ph-no-autocapture>
        <AlertsList aria-hidden>
          <AlertSkeletonIcon />
          <AlertSkeletonIcon />
        </AlertsList>
      </AlertsContainer>
    );
  }

  const alerts = data?.alerts ?? [];
  if (isError || alerts.length === 0) {
    return null;
  }

  const title = t('result.preventions.title');
  const handleValueChange = (value: string) => {
    const nextOpen = value === ITEM_VALUE;
    setOpen(nextOpen);
    writeStoredOpen(storageKey, nextOpen);
  };

  return (
    <SectionCard data-ph-no-autocapture>
      <SectionAccordion
        type="single"
        collapsible
        value={open ? ITEM_VALUE : ''}
        onValueChange={handleValueChange}
      >
        <SectionItem value={ITEM_VALUE}>
          <SectionTrigger>
            <TriggerRow>
              <TriggerTitle>{title}</TriggerTitle>
              {!open && (
                <CollapsedIcons>
                  {alerts.map((alert) => (
                    <AlertItem key={alert.key} alert={alert} />
                  ))}
                </CollapsedIcons>
              )}
            </TriggerRow>
          </SectionTrigger>
          <SectionContent>
            <RowList aria-label={title}>
              {alerts.map((alert) => {
                if (alert.gopCode === GOPCode.GOP_03360) {
                  return <GeriatricRow key={alert.key} alert={alert} />;
                }
                const name = t(`result.preventions.names.${alert.key}`, {
                  defaultValue: alert.key,
                });
                if (alert.key === INFLUENZA_ALERT_KEY) {
                  return (
                    <InfluenzaRow
                      key={alert.key}
                      alert={alert}
                      name={name}
                      onRecord={() => onRecordInfluenza?.(alert)}
                    />
                  );
                }
                if (LAST_PERFORMED_GOPS.includes(alert.gopCode)) {
                  return <LastPerformedRow key={alert.key} alert={alert} name={name} />;
                }
                if (ONE_TIME_GOPS.includes(alert.gopCode)) {
                  return <OneTimeRow key={alert.key} alert={alert} name={name} />;
                }
                return <PreventionRow key={alert.key} alert={alert} name={name} />;
              })}
            </RowList>
          </SectionContent>
        </SectionItem>
      </SectionAccordion>
    </SectionCard>
  );
};
